import { useState } from 'react';

import * as navigation from 'app-level/navigation';
import { filtersToQueryFilters, PillFilters } from 'features/common-elements';
import { CashFlows } from 'features/drilldown/cashflows/CashFlows';
import { getUsePortfolioCashflow } from 'features/drilldown/cashflows/gql/usePortfolioCashflows_gql';
import { PortfolioFilterConfig } from 'features/pages/portfolio/Portfolio/portfolioFilters.config';

import { getUseSelectedPoolCashflow } from './getSelectedPoolCashflow_gql';
import { Dropdown } from '../../../../ui-kit';
import { usePoolOptions } from '../hooks/usePoolOptions';
import { usePoolSelection } from '../hooks/usePoolSelection';
import { useSyncSelectedPoolsData } from '../hooks/useSyncSelectedPoolsData';
import { PortfolioLayout } from '../Portfolio/PortfolioLayout';
import { usePortfolioFilters } from '../Portfolio/usePortfolioFilters';

import { GetPools } from 'query/__generated__/getPools';
type Listing = NonNullable<
  NonNullable<NonNullable<NonNullable<GetPools>['user']>['company']>['pools']
>[number];
const CashFlowModeling = () => {
  const assetClass = navigation.usePortfolioAssetClass();
  const filterProps = usePortfolioFilters();
  const { setPool } = filterProps;
  const [selectedPool, setSelectedPool] = useState<{
    label: string;
    value: string;
    poolData: Listing;
  } | null>(null);

  const { poolOptions } = usePoolOptions({ assetClass });

  useSyncSelectedPoolsData(selectedPool ? [selectedPool.poolData] : []);

  // Initialize pool from URL and clear the selection when the asset class changes.
  usePoolSelection({ setSelectedPool });

  if (!assetClass || !filterProps.filtersConfig) {
    return <PortfolioLayout />;
  }

  const queryFilters = filtersToQueryFilters<PortfolioFilterConfig>(
    filterProps.currentFilters,
    filterProps.filtersConfig,
  );
  const useCashflowSummery = getUsePortfolioCashflow(assetClass, queryFilters);
  const userSettingKey = `cashflowsportfolio${assetClass}`;
  const useSelectedPoolCashflow = getUseSelectedPoolCashflow(assetClass, queryFilters);

  const handlePoolChange = (
    option: { label: string; value: string; poolData?: Listing } | null,
  ) => {
    if (!option || !option.poolData) {
      setPool(null);
      setSelectedPool(null);
      return;
    }

    setSelectedPool(
      option as {
        label: string;
        value: string;
        poolData: Listing;
      },
    );
    setPool(option.value);
  };

  return (
    <PortfolioLayout
      filterRow={
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex-1">
            <PillFilters {...filterProps} filtersConfig={filterProps.filtersConfig} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Pool:</span>
            <Dropdown
              options={poolOptions || []}
              value={selectedPool?.label || ''}
              className="w-48 border-none"
              placeholder="Select Pool"
              onValueChange={handlePoolChange}
            />
          </div>
        </div>
      }
    >
      <CashFlows
        key={assetClass}
        settingKey={userSettingKey}
        useSummaryData={useCashflowSummery}
        selectedPoolId={selectedPool?.value}
        selectedPoolCashflow={useSelectedPoolCashflow}
      />
    </PortfolioLayout>
  );
};

export { CashFlowModeling };
